import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Leaf } from 'lucide-react';
import apiClient from '../utils/apiClient';

const PaymentSuccess = () => {
  const [isUpdating, setIsUpdating] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        // Get session ID from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        
        if (sessionId) {
          console.log('Payment successful, verifying and auto-logging in...');
          
          // Call the new verify-payment endpoint to auto-login
          const response = await apiClient.post('/auth/verify-payment', {
            session_id: sessionId
          });
          
          // Check if payment is still required (user is inactive)
          if (response.requires_payment) {
            console.log('Payment still required, redirecting to checkout...');
            window.location.href = response.checkout_url;
            return;
          }
          
          if (response.success) {
            // Store auth data securely using the auth context's helper functions
            // The auth context will validate this token on next app load
            localStorage.setItem('auth_token', response.token);
            localStorage.setItem('user_data', JSON.stringify(response.user));
            localStorage.setItem('subscription_data', JSON.stringify(response.subscription));
            
            console.log('Auto-login successful!');
            
            // Wait a moment to show success, then redirect to leaderboard
            // The auth context will pick up the stored data and validate it
            setTimeout(() => {
              window.location.href = '/';
            }, 2000);
          } else {
            throw new Error('Payment verification failed');
          }
        } else {
          throw new Error('No session ID found');
        }
      } catch (error) {
        console.error('Error handling payment success:', error);
        setError('There was an issue completing your registration. Please contact support.');
      } finally {
        setIsUpdating(false);
      }
    };

    handlePaymentSuccess();
  }, []);

  const handleContinue = () => {
    // If there was an error, redirect to login page
    if (error) {
      window.location.href = '/login';
    } else {
      // Otherwise redirect to main app
      window.location.href = '/';
    }
  };

  if (isUpdating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">Please wait while we activate your premium subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-center mb-4">
            <Leaf className="text-green-600 mr-2" size={24} />
            <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              WeederBoard
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          
          <p className="text-gray-600 mb-6">
            {error ? error : 'Welcome to WeederBoard Premium! Your account has been created and you are now logged in. Redirecting you to the app...'}
          </p>

          {/* Premium Features */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-green-800 mb-2">Premium Features Unlocked:</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Unlimited cannabis deal searches</li>
              <li>• Advanced filtering options</li>
            </ul>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
          >
            {error ? 'Go to Login' : 'Continue to App'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>

          <p className="text-sm text-gray-500 mt-4">
            You will receive an email confirmation shortly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
